import { getTumblerConfig } from '../../../utils/appConfig';
import { getPiezoClient } from '../provisioning/PiezoClient';
import { PiezoDebugPrefix } from '../provisioning/PiezoClient.constants';
import { getPiezoWorkerPool, MidwayAuthRequiredError } from '../pool/workerPool';
import { isMidwayAuthError, recordMidwaySessionExpired } from './midwaySession';
import type { PiezoGetPiezoExecutionContextInput } from '../provisioning/PiezoClient.types';

/** Fixed for the OpsTumbler Mechanic namespace. */
const PIEZO_NAMESPACE_ID = 'opstumbler';
const PIEZO_STACK_DOMAIN = 'prod';

export interface CreateToolPiezoArtifactParams {
  /** Mechanic tool path, e.g. 'aes/domain/dp/node/es/curl/get-filtered-response'. */
  toolId: string;
  /**
   * The EXACT tool parameters the proxy will execute. Signed as { parameters: <this> }, so its canonical
   * (JCS) form must match the arguments TumblerApi.toExecutePayload() sends.
   */
  parameters: Record<string, unknown>;
}

/**
 * Creates an operator-signed Piezo artifact bundle for a single migrated-tool invocation and
 * returns it as a base64 string.
 *
 * Signs over the exact { parameters } payload the proxy will run, so the RFC-8785/JCS parameter
 * hash the backend PiezoProvider recomputes always matches. The cluster/stage/partition context is read from getTumblerConfig() (the same
 * server-injected window.__APP_CONFIG__ that AppContext is built from), so callers only pass the
 * tool path and its parameters. Everything else the signer needs (operator key, decks, Notary
 * permits, Witness deckbox, policy/tool indexes) is already provisioned by PiezoClient.init();
 * Witness runs by default (mandatory V2) — disableWitness is intentionally not set.
 *
 * Signs whatever it is given: piezoLink decides what Piezo requires, so a tool that reaches here is one
 * that must be signed. Do not call this for a tool `isToolPiezoRequired` rejects.
 *
 * Best-effort: returns undefined on any failure (Piezo not initialized, no policy mapped for the
 * tool, signing/Witness error) so callers can proceed without blocking the operator.
 *
 * @returns the inline base64 ArtifactBundle, or undefined if it could not be created.
 */
export async function createToolPiezoArtifact(
  params: CreateToolPiezoArtifactParams,
): Promise<string | undefined> {
  const { toolId, parameters } = params;
  try {
    const piezoClient = getPiezoClient();

    const config = getTumblerConfig();
    const region = config.current;

    await piezoClient.init({ region: region.publicRegion });

    const resolution = piezoClient.resolvePolicyForTool(toolId);
    if (resolution.ok === false) {
      console.warn(
        `${PiezoDebugPrefix} no eligible policy for tool ${toolId} (${resolution.failure}); proceeding ` +
          `without artifact. resolvePolicyForTool has already logged the candidate principals.`,
      );
      return undefined;
    }
    const policyHash = resolution.policyKey;

    const payloadString = JSON.stringify({ parameters: parameters ?? {} });

    const input: PiezoGetPiezoExecutionContextInput = {
      toolId,
      policyHash,
      cluster: {
        id: `${region.airportCode}${config.stage}`.toLowerCase(),
        region: region.publicRegion,
      },
      namespaceId: PIEZO_NAMESPACE_ID,
      stackDomain: PIEZO_STACK_DOMAIN,
      partition: region.partition,
      payloadString,
    };

    try {
      return await getPiezoWorkerPool().sign(input);
    } catch (poolErr) {
      if (poolErr instanceof MidwayAuthRequiredError) {
        recordMidwaySessionExpired();
        console.warn(
          `${PiezoDebugPrefix} worker reported MIDWAY_AUTH_REQUIRED for ${toolId}; the operator's ` +
            `Midway session is gone and only they can renew it`,
        );
        return undefined;
      }
      console.warn(
        `${PiezoDebugPrefix} signing ${toolId} on the main thread instead of a worker:`,
        poolErr,
      );
    }

    const { ArtifactBundle } = await piezoClient.getPiezoExecutionContext(input);
    return ArtifactBundle;
  } catch (err) {
    if (isMidwayAuthError(err)) {
      recordMidwaySessionExpired();
    }
    console.warn(`${PiezoDebugPrefix} failed to create artifact for tool ${toolId}:`, err);
    return undefined;
  }
}
